import { z } from 'zod';
export const SCIM_BASE_PATH = '/api/public/v1/scim/v2';
/** Every SCIM bearer token starts with this; 32 CSPRNG bytes base64url follow (43 chars). */
export const SCIM_TOKEN_PREFIX = 'inxs_';
export const SCIM_CONTENT_TYPE = 'application/scim+json';
export const SCIM_LIST_DEFAULT_COUNT = 100;
export const SCIM_LIST_MAX_COUNT = 200;
/** Per tenant per minute (advisory, per sandbox — docs/008 S10). */
export const SCIM_RATE_LIMIT_PER_MINUTE = 600;
export const SCIM_SCHEMA = {
    USER: 'urn:ietf:params:scim:schemas:core:2.0:User',
    ENTERPRISE_USER: 'urn:ietf:params:scim:schemas:extension:enterprise:2.0:User',
    LIST_RESPONSE: 'urn:ietf:params:scim:api:messages:2.0:ListResponse',
    PATCH_OP: 'urn:ietf:params:scim:api:messages:2.0:PatchOp',
    ERROR: 'urn:ietf:params:scim:api:messages:2.0:Error',
    SERVICE_PROVIDER_CONFIG: 'urn:ietf:params:scim:schemas:core:2.0:ServiceProviderConfig',
    RESOURCE_TYPE: 'urn:ietf:params:scim:schemas:core:2.0:ResourceType',
    SCHEMA: 'urn:ietf:params:scim:schemas:core:2.0:Schema',
};
/** True for SCIM_BASE_PATH itself or anything under `${SCIM_BASE_PATH}/` (segment boundary; query string ignored). */
export function isScimPath(path) {
    const pathOnly = path.split('?')[0] ?? '';
    return pathOnly === SCIM_BASE_PATH || pathOnly.startsWith(`${SCIM_BASE_PATH}/`);
}
/** SCIM clients send booleans as JSON booleans or as "True"/"False" strings (Entra). Case-insensitive. */
export const scimBooleanSchema = z.union([
    z.boolean(),
    z
        .string()
        .regex(/^(true|false)$/i)
        .transform((v) => v.toLowerCase() === 'true'),
]);
/**
 * NOT .strict(), on purpose: RFC 7644 §3.3 lets a client send attributes we do not store (`password`,
 * `locale`, `groups`, `addresses`…), and refusing them would break Okta and Entra. z.object strips them.
 */
export const scimMultiValueSchema = z.object({
    value: z.string().trim().max(320),
    type: z.string().max(64).optional(),
    primary: scimBooleanSchema.optional(),
});
/** A SCIM address (RFC 7643 §4.1.2). We keep only what locates a person; other sub-attributes are stripped. */
export const scimAddressSchema = z.object({
    type: z.string().max(64).optional(),
    primary: scimBooleanSchema.optional(),
    formatted: z.string().trim().max(500).optional(),
    locality: z.string().trim().max(200).optional(),
});
export const scimEnterpriseUserSchema = z.object({
    department: z.string().max(500).optional(),
    manager: z.union([z.object({ value: z.string().max(255).optional() }), z.string().max(255)]).optional(),
});
export const scimUserInputSchema = z.object({
    schemas: z.array(z.string().max(255)).max(10).optional(),
    userName: z.string().trim().min(1).max(320),
    externalId: z.string().max(255).optional(),
    name: z
        .object({
        givenName: z.string().max(200).optional(),
        familyName: z.string().max(200).optional(),
        formatted: z.string().max(401).optional(),
    })
        .optional(),
    displayName: z.string().max(401).optional(),
    title: z.string().max(500).optional(),
    active: scimBooleanSchema.optional(),
    emails: z.array(scimMultiValueSchema).max(20).optional(),
    phoneNumbers: z.array(scimMultiValueSchema.extend({ value: z.string().trim().max(64) })).max(20).optional(),
    addresses: z.array(scimAddressSchema).max(10).optional(),
    [SCIM_SCHEMA.ENTERPRISE_USER]: scimEnterpriseUserSchema.optional(),
});
export const SCIM_PATCH_OPS = ['add', 'replace', 'remove'];
/** `op` is case-insensitive (Entra sends "Replace"); normalized to lower case. */
export const scimPatchOperationSchema = z.object({
    op: z
        .string()
        .transform((v) => v.toLowerCase())
        .pipe(z.enum(SCIM_PATCH_OPS)),
    path: z.string().trim().min(1).max(512).optional(),
    value: z.unknown().optional(),
});
export const scimPatchRequestSchema = z.object({
    schemas: z.array(z.string().max(255)).max(10).optional(),
    Operations: z.array(scimPatchOperationSchema).min(1).max(100),
});
/** GET /Users query. Clamping (startIndex < 1 → 1; count < 0 → 0; count > MAX → MAX) is the service's job, per RFC 7644 §3.4.2.4. */
export const scimListQuerySchema = z.object({
    filter: z.string().max(512).optional(),
    startIndex: z.coerce.number().int().optional(),
    count: z.coerce.number().int().optional(),
});
/** The User we return. Strict: a response is an allow-list (S7). */
export const scimUserResourceSchema = z
    .object({
    schemas: z.array(z.string()).min(1),
    id: z.string().min(1),
    userName: z.string().min(1),
    name: z.object({ givenName: z.string(), familyName: z.string(), formatted: z.string() }).strict(),
    displayName: z.string(),
    active: z.boolean(),
    emails: z.array(z.object({ value: z.string(), type: z.literal('work'), primary: z.literal(true) }).strict()),
    phoneNumbers: z.array(z.object({ value: z.string(), type: z.literal('mobile') }).strict()),
    title: z.string().optional(),
    [SCIM_SCHEMA.ENTERPRISE_USER]: z
        .object({
        department: z.string().optional(),
        manager: z.object({ value: z.string() }).strict().optional(),
    })
        .strict()
        .optional(),
    meta: z
        .object({
        resourceType: z.literal('User'),
        created: z.string().datetime(),
        lastModified: z.string().datetime(),
    })
        .strict(),
})
    .strict();
export const scimUserListResponseSchema = z
    .object({
    schemas: z.tuple([z.literal(SCIM_SCHEMA.LIST_RESPONSE)]),
    totalResults: z.number().int().nonnegative(),
    startIndex: z.number().int().min(1),
    itemsPerPage: z.number().int().nonnegative(),
    Resources: z.array(scimUserResourceSchema),
})
    .strict();
export const SCIM_ERROR_TYPES = [
    'invalidFilter',
    'tooMany',
    'uniqueness',
    'mutability',
    'invalidSyntax',
    'invalidPath',
    'noTarget',
    'invalidValue',
    'invalidVers',
    'sensitive',
];
export const scimErrorSchema = z
    .object({
    schemas: z.tuple([z.literal(SCIM_SCHEMA.ERROR)]),
    status: z.string().regex(/^[1-5]\d\d$/),
    scimType: z.enum(SCIM_ERROR_TYPES).optional(),
    detail: z.string().min(1), // our stable ErrorCode, never prose (S8)
})
    .strict();
/** SCIM_INVALID_FILTER → invalidFilter; SCIM_INVALID_PATCH → invalidPath; SCIM_UNIQUENESS → uniqueness;
 *  VALIDATION_FAILED → invalidValue; anything else → undefined (no scimType). */
export function scimTypeForErrorCode(code) {
    switch (code) {
        case 'SCIM_INVALID_FILTER':
            return 'invalidFilter';
        case 'SCIM_INVALID_PATCH':
            return 'invalidPath';
        case 'SCIM_UNIQUENESS':
            return 'uniqueness';
        case 'VALIDATION_FAILED':
            return 'invalidValue';
        default:
            return undefined;
    }
}
/** RFC 7643 §5. */
export const SCIM_SERVICE_PROVIDER_CONFIG = {
    schemas: [SCIM_SCHEMA.SERVICE_PROVIDER_CONFIG],
    patch: { supported: true },
    bulk: { supported: false, maxOperations: 0, maxPayloadSize: 0 },
    filter: { supported: true, maxResults: SCIM_LIST_MAX_COUNT },
    changePassword: { supported: false },
    sort: { supported: false },
    etag: { supported: false },
    authenticationSchemes: [
        {
            type: 'oauthbearertoken',
            name: 'OAuth Bearer Token',
            description: 'A per-tenant bearer token generated in InstaCheckin under Settings, Directory sync.',
            primary: true,
        },
    ],
    meta: { resourceType: 'ServiceProviderConfig', location: `${SCIM_BASE_PATH}/ServiceProviderConfig` },
};
/** RFC 7643 §6, as a ListResponse of one. */
export const SCIM_RESOURCE_TYPES_RESPONSE = {
    schemas: [SCIM_SCHEMA.LIST_RESPONSE],
    totalResults: 1,
    startIndex: 1,
    itemsPerPage: 1,
    Resources: [
        {
            schemas: [SCIM_SCHEMA.RESOURCE_TYPE],
            id: 'User',
            name: 'User',
            endpoint: '/Users',
            description: 'User Account',
            schema: SCIM_SCHEMA.USER,
            schemaExtensions: [{ schema: SCIM_SCHEMA.ENTERPRISE_USER, required: false }],
            meta: { resourceType: 'ResourceType', location: `${SCIM_BASE_PATH}/ResourceTypes/User` },
        },
    ],
};
function attr(name, type, extra = {}) {
    return {
        name,
        type,
        multiValued: false,
        required: false,
        caseExact: false,
        mutability: 'readWrite',
        returned: 'default',
        uniqueness: 'none',
        ...extra,
    };
}
/** RFC 7643 §7, as a ListResponse of two: the User attributes and the Enterprise attributes this server reads. */
export const SCIM_SCHEMAS_RESPONSE = {
    schemas: [SCIM_SCHEMA.LIST_RESPONSE],
    totalResults: 2,
    startIndex: 1,
    itemsPerPage: 2,
    Resources: [
        {
            schemas: [SCIM_SCHEMA.SCHEMA],
            id: SCIM_SCHEMA.USER,
            name: 'User',
            description: 'User Account',
            attributes: [
                attr('userName', 'string', { required: true, uniqueness: 'server' }),
                attr('name', 'complex', {
                    subAttributes: [attr('givenName', 'string'), attr('familyName', 'string'), attr('formatted', 'string')],
                }),
                attr('displayName', 'string'),
                attr('title', 'string'),
                attr('active', 'boolean'),
                attr('emails', 'complex', {
                    multiValued: true,
                    subAttributes: [attr('value', 'string'), attr('type', 'string'), attr('primary', 'boolean')],
                }),
                attr('phoneNumbers', 'complex', {
                    multiValued: true,
                    subAttributes: [attr('value', 'string'), attr('type', 'string')],
                }),
                attr('addresses', 'complex', {
                    multiValued: true,
                    subAttributes: [
                        attr('type', 'string'),
                        attr('primary', 'boolean'),
                        attr('formatted', 'string'),
                        attr('locality', 'string'),
                    ],
                }),
            ],
            meta: { resourceType: 'Schema', location: `${SCIM_BASE_PATH}/Schemas/${SCIM_SCHEMA.USER}` },
        },
        {
            schemas: [SCIM_SCHEMA.SCHEMA],
            id: SCIM_SCHEMA.ENTERPRISE_USER,
            name: 'EnterpriseUser',
            description: 'Enterprise User',
            attributes: [
                attr('department', 'string'),
                attr('manager', 'complex', { subAttributes: [attr('value', 'string')] }),
            ],
            meta: { resourceType: 'Schema', location: `${SCIM_BASE_PATH}/Schemas/${SCIM_SCHEMA.ENTERPRISE_USER}` },
        },
    ],
};
//# sourceMappingURL=scim.js.map